import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import instrumentsService from '../services/instrumentsService';
import websocketService from '../services/websocketService';
import { showToast } from '../store/uiSlice';
import { updateUser } from '../store/authSlice';
import {
  updatePosition,
  removePosition,
  setBalance,
  setPortfolio,
  setHistoryOrders,
  setPendingOrders,
} from '../store/tradeSlice';
import api from '../services/api';

/**
 * Normalize a trade record coming from REST API or WebSocket so the
 * frontend always sees the same shape regardless of source.
 *
 * Rule: direction is ALWAYS the ORIGINAL position direction.
 *   'buy'  = was a LONG position → show "LONG"
 *   'sell' = was a SHORT position → show "SHORT"
 */
const normalizeTrade = (t) => ({
  ...t,
  // unify instrumentId field
  instId:       t.instId       || t.instrumentId,
  instrumentId: t.instrumentId || t.instId,
  // keep direction as-is — backend now always stores original direction
  direction:    t.direction,
  // unify time fields
  openTime:     t.openTime  || t.time       || null,
  closeTime:    t.closeTime || null,
  // numeric coercions
  pnl:          Number(t.pnl)        || 0,
  swap:         Number(t.swap)       || 0,
  commission:   Number(t.commission) || 0,
  qty:          Number(t.qty)        || 0,
  entryPrice:   Number(t.entryPrice) || 0,
  exitPrice:    Number(t.exitPrice)  || 0,
  sl:           Number(t.sl)         || 0,
  tp:           Number(t.tp)         || 0,
});

export const useInitializeApp = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  // ── Load instruments once ────────────────────────────────────────────────
  useEffect(() => {
    const loadInstruments = async () => {
      try {
        if (!instrumentsService.isLoaded()) {
          await instrumentsService.fetchInstruments();
        }
      } catch (error) {
        // silently fail
      }
    };
    loadInstruments();
  }, []);

  // ── WebSocket + initial data ─────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;

    websocketService.connect();
    websocketService.subscribeUser(user.id);

    // ── Fetch initial data from REST ──────────────────────────────────────
    const fetchInitialData = async () => {
      try {
        // Portfolio (open positions)
        const posRes = await api.get('/user/portfolio');
        if (posRes.data) {
          const portfolio = {};
          if (Array.isArray(posRes.data)) {
            posRes.data.forEach(pos => {
              if (pos.instrumentId) portfolio[pos.instrumentId] = pos;
            });
          } else if (typeof posRes.data === 'object') {
            Object.entries(posRes.data).forEach(([key, pos]) => {
              portfolio[key] = pos;
            });
          }
          dispatch(setPortfolio(portfolio));
        }

        // Balance
        const balRes = await api.get('/user/balance');
        if (balRes.data?.balance !== undefined) {
          dispatch(setBalance(Number(balRes.data.balance)));
        }

        // History — ALL closed trades (no limit)
        const tradeRes = await api.get('/user/trades?limit=999999&status=closed');
        if (tradeRes.data?.trades) {
          // Deduplicate by ID
          const trades = tradeRes.data.trades.map(normalizeTrade);
          const uniqueTrades = trades.filter((trade, index, self) =>
            index === self.findIndex((t) => t.id === trade.id)
          );
          dispatch(setHistoryOrders(uniqueTrades));
        }

        // Pending orders
        const orderRes = await api.get('/user/pending-orders');
        if (orderRes.data?.orders) {
          dispatch(setPendingOrders(orderRes.data.orders));
        }
      } catch (e) {
        // silently fail
      }
    };

    // ── WebSocket event handlers ──────────────────────────────────────────

    const handlePositionUpdate = (data) => {
      if (data?.instrumentId) {
        dispatch(updatePosition({ instrumentId: data.instrumentId, position: data }));
      }
    };
    websocketService.on('position_update', handlePositionUpdate);

    const handlePositionsUpdate = (data) => {
      if (data?.positions) {
        const portfolio = {};
        data.positions.forEach(pos => {
          if (pos.instrumentId) portfolio[pos.instrumentId] = pos;
        });
        dispatch(setPortfolio(portfolio));
      }
    };
    websocketService.on('positions_update', handlePositionsUpdate);

    /**
     * position_closed — fired by backend when any position is fully closed.
     * We do NOT add to history here — refreshUserData in TradingTerminal handles it.
     * This prevents duplicate entries.
     */
    const handlePositionClosed = (data) => {
      if (!data?.instrumentId) return;

      // Remove from active positions
      dispatch(removePosition(data.instrumentId));

      // Update balance if provided
      if (data.newBalance !== undefined) {
        dispatch(setBalance(Number(data.newBalance)));
      }

      // Trigger a full data refresh (history will be re-fetched from API)
      window.dispatchEvent(new CustomEvent('trade-completed'));

      const pnl = data.pnl || 0;
      dispatch(showToast({
        message:  `Position closed: ${data.instrumentId} · P&L: ${pnl >= 0 ? '+' : ''}$${Math.abs(pnl).toFixed(2)}`,
        type:     pnl >= 0 ? 'success' : 'error',
        duration: 5000,
      }));
    };
    websocketService.on('position_closed', handlePositionClosed);

    const handleBalanceUpdate = (data) => {
      if (data?.balance !== undefined) dispatch(setBalance(Number(data.balance)));
    };
    websocketService.on('balance_update', handleBalanceUpdate);

    const handleBalance = (data) => {
      if (data?.balance !== undefined) dispatch(setBalance(Number(data.balance)));
    };
    websocketService.on('balance', handleBalance);

    const handleTradeNotification = (data) => {
      if (data?.type === 'close_position') {
        const pnl = Number(data.pnl) || 0;
        dispatch(showToast({
          message:  `Position closed: ${data.instrumentId} · P&L: ${pnl >= 0 ? '+' : ''}$${Math.abs(pnl).toFixed(2)}`,
          type:     pnl >= 0 ? 'success' : 'error',
          duration: 5000,
        }));
      }
    };
    websocketService.on('trade_notification', handleTradeNotification);

    // trade_history WebSocket push — closed only
    const handleTradeHistory = (data) => {
      if (data?.trades) {
        const closedOnly = data.trades
          .filter(t => t.status === 'closed')
          .map(normalizeTrade);
        // Deduplicate before dispatching
        const uniqueTrades = closedOnly.filter((trade, index, self) =>
          index === self.findIndex((t) => t.id === trade.id)
        );
        dispatch(setHistoryOrders(uniqueTrades));
      }
    };
    websocketService.on('trade_history', handleTradeHistory);

    const handlePendingOrders = (data) => {
      if (data?.orders) dispatch(setPendingOrders(data.orders));
    };
    websocketService.on('pending_orders', handlePendingOrders);

    const handleOrderCancelled = (data) => {
      if (data?.orderId) {
        dispatch(showToast({ message: 'Order cancelled', type: 'info', duration: 2000 }));
      }
    };
    websocketService.on('order_cancelled', handleOrderCancelled);

    const handleError = (data) => {
      if (data?.message) {
        dispatch(showToast({ message: data.message, type: 'error', duration: 5000 }));
      }
    };
    websocketService.on('error', handleError);

    const handleStatusChange = (status) => {
      if (status === 'connected') {
        fetchInitialData();
        dispatch(showToast({ message: 'Connected to live market data', type: 'success', duration: 2000 }));
      } else if (status === 'disconnected') {
        dispatch(showToast({ message: 'Disconnected from market data', type: 'warning', duration: 3000 }));
      } else if (status === 'error' || status === 'failed') {
        dispatch(showToast({ message: 'Connection lost. Reconnecting...', type: 'error', duration: 5000 }));
      }
    };
    websocketService.onStatusChange(handleStatusChange);

    // ─── Handle badge/profile updates from admin (real-time) ───────────────
    const handleUserUpdated = (data) => {
      if (data?.tag) {
        dispatch(showToast({
          message: data.message || `Your badge has been updated to ${data.tag}!`,
          type: 'success',
          duration: 3000
        }));
        
        dispatch(updateUser({ tag: data.tag }));
        
        api.get('/user/profile')
          .then(res => {
            if (res.data) {
              dispatch(updateUser(res.data));
            }
          })
          .catch(() => {});
      }
    };
    websocketService.on('user_updated', handleUserUpdated);

    return () => {
      websocketService.off('position_update',    handlePositionUpdate);
      websocketService.off('positions_update',   handlePositionsUpdate);
      websocketService.off('position_closed',    handlePositionClosed);
      websocketService.off('balance_update',     handleBalanceUpdate);
      websocketService.off('balance',            handleBalance);
      websocketService.off('trade_notification', handleTradeNotification);
      websocketService.off('trade_history',      handleTradeHistory);
      websocketService.off('pending_orders',     handlePendingOrders);
      websocketService.off('order_cancelled',    handleOrderCancelled);
      websocketService.off('user_updated',       handleUserUpdated);
      websocketService.off('error',              handleError);
      websocketService.offStatusChange(handleStatusChange);
      websocketService.unsubscribeUser(user.id);
    };
  }, [isAuthenticated, user, dispatch]);
};

export default useInitializeApp;